<template>
  <aside class="rmr-notice" :class="`rmr-notice--${variant}`">
    <div class="rmr-notice__icon">
      <v-icon :color="resolvedColor" size="20">
        {{ resolvedIcon }}
      </v-icon>
    </div>

    <div class="rmr-notice__content">
      <div v-if="title" class="rmr-notice__title">{{ title }}</div>
      <div class="rmr-notice__text">
        <slot>{{ text }}</slot>
      </div>
    </div>
  </aside>
</template>

<script>
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCheckCircleOutline,
  mdiInformationOutline,
} from "@mdi/js";
import {
  RMR_DANGER,
  RMR_GREEN,
  RMR_INDIGO,
  RMR_ORANGE,
  RMR_TEXT_SECONDARY,
} from "@/styles/tokens";

const VARIANTS = {
  info: { color: RMR_INDIGO, icon: mdiInformationOutline },
  warning: { color: RMR_ORANGE, icon: mdiAlertOutline },
  success: { color: RMR_GREEN, icon: mdiCheckCircleOutline },
  error: { color: RMR_DANGER, icon: mdiAlertCircleOutline },
  neutral: { color: RMR_TEXT_SECONDARY, icon: mdiInformationOutline },
};

export default {
  name: "RmrNotice",
  props: {
    title: {
      type: String,
      default: "",
    },
    text: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "info",
      validator: (value) => Object.keys(VARIANTS).includes(value),
    },
    icon: {
      type: String,
      default: "",
    },
  },
  computed: {
    resolvedColor() {
      return VARIANTS[this.variant].color;
    },
    resolvedIcon() {
      return this.icon || VARIANTS[this.variant].icon;
    },
  },
};
</script>

<style lang="scss" scoped>
.rmr-notice {
  display: flex;
  align-items: flex-start;
  gap: $rmr-space-sm;
  width: 100%;
  padding: $rmr-space-sm $rmr-space-md;
  border: 1px solid;
  border-radius: $rmr-radius-xl;
  box-sizing: border-box;
}

.rmr-notice--info {
  background: $rmr-indigo-bg;
  border-color: $rmr-indigo-border;
}

.rmr-notice--warning {
  background: $rmr-ca-soft;
  border-color: $rmr-ca-tint;
}

.rmr-notice--success {
  background: $rmr-green-bg;
  border-color: rgba($rmr-green, 0.25);
}

.rmr-notice--error {
  background: $rmr-danger-bg;
  border-color: rgba($rmr-danger, 0.25);
}

.rmr-notice--neutral {
  background: $rmr-surface-2;
  border-color: $rmr-border-hover;
}

.rmr-notice__icon {
  display: flex;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: $rmr-surface-0;
  border-radius: $rmr-radius-lg;
  box-shadow: $rmr-shadow-card;
}

.rmr-notice__content {
  min-width: 0;
  padding-top: 1px;
}

.rmr-notice__title {
  color: $rmr-text-base;
  font-size: $rmr-fs-title;
  font-weight: 800;
  line-height: 1.35;
}

.rmr-notice__text {
  margin-top: 2px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-body;
  font-weight: 500;
  line-height: 1.5;
}
</style>
